import React, { useEffect, useRef, useState } from 'react';
import CorpInfo from './CorpInfo';
import ContactPanel from './ContactPanel';
import { fetchWithCorpId } from '../utils/api';
import { CONTACT_OWNERS, listContacts, saveContacts } from '../utils/contacts';
import { FormLoading } from './ui/formkit';
import StepFlow, { FlowNote, TabAttentionDot } from './ui/StepFlow';
import { useToast } from './ui/Toast';
import { toDateInputValue } from '../utils/dateUtils';

/**
 * Used in two contexts:
 *   1. Standalone page (via CorpFormScreen, which fetches the record and
 *      passes it as `initialData`) → renders its own Save/Cancel buttons.
 *   2. Inside a Drawer (from CorpTable) → id from `corpId` prop, fetches
 *      itself; pass `formRef` so the drawer footer can trigger submit and
 *      `hideActions` to skip the inline buttons.
 *
 * Create mode is a two-step flow (Details, Contacts) and always renders its
 * own step footer; edit mode keeps the tabs.
 */
const DEFAULT_CORP = {
  name: '', address: '', city: '', state: '', zip: '',
  status: '', startDate: '', updateDate: '', contacts: [],
};

const CREATE_STEPS = ['Details', 'Contacts'];

const CONTACTS_RULE = "These contacts appear on this corporation's invoices and emails.";
const EMAIL_RE = /^\S+@\S+\.\S+$/;

function CorpForm({
  mode = 'create',
  initialData,
  corpId,                  // optional id; if set, CorpForm fetches itself
  formRef,                 // optional ref on <form>; lets a parent trigger submit
  hideActions = false,     // hide inline Save/Cancel (edit mode only; the create stepper always owns its footer)
  onSuccess,
  onCancel,
}) {
  const toast = useToast();
  const isEdit = mode === 'edit';
  const [activeTab, setActiveTab] = useState('info');   // edit mode tabs
  const [step, setStep] = useState(1);                  // create mode stepper (1..2)
  const [corp, setCorp] = useState(DEFAULT_CORP);
  const [message, setMessage] = useState('');
  const [loadingRecord, setLoadingRecord] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  // Snapshot of the last saved state; drives the drawer's discard guard.
  // In edit mode it is set once the record loads.
  const initialSnapshotRef = useRef(isEdit ? null : JSON.stringify(DEFAULT_CORP));
  const loadedContactsRef = useRef([]);

  // Screen path: CorpFormScreen fetches the record and passes it in.
  useEffect(() => {
    if (!initialData) return;
    const normalized = {
      ...DEFAULT_CORP,
      ...initialData,
      startDate: toDateInputValue(initialData.startDate),
      contacts: initialData.contacts || [],
    };
    setCorp(normalized);
    initialSnapshotRef.current = JSON.stringify(normalized);
    setLoadingRecord(false);
  }, [initialData]);

  // Drawer path: fetch the corp ourselves if a corpId was passed.
  useEffect(() => {
    const fetchCorp = async () => {
      if (isEdit && corpId) {
        setLoadingRecord(true);
        try {
          const data = await fetchWithCorpId(`/corps/${corpId}`);
          // contacts come from their own endpoint since backend #148
          const loadedContacts = await listContacts(CONTACT_OWNERS.CORP, corpId);
          loadedContactsRef.current = loadedContacts;
          const normalized = {
            ...DEFAULT_CORP,
            ...data,
            startDate: toDateInputValue(data.startDate),
            contacts: loadedContacts,
          };
          setCorp(normalized);
          initialSnapshotRef.current = JSON.stringify(normalized);
        } catch (error) {
          setMessage('Could not load this corporation: ' + error.message);
        } finally {
          setLoadingRecord(false);
        }
      } else {
        setLoadingRecord(false);
      }
    };
    fetchCorp();
  }, [isEdit, corpId]);

  const isDirty = initialSnapshotRef.current !== null
    && JSON.stringify(corp) !== initialSnapshotRef.current;

  const hasContacts = (corp.contacts || []).length > 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCorp(prev => ({ ...prev, [name]: value }));
  };

  const goToStep = (n) => {
    setMessage('');
    setStep(n);
  };

  // Advance the create stepper; validates the current step and blocks with an
  // inline error on failure.
  const advanceStep = () => {
    if (step === 1) {
      if (!(corp.name || '').trim()) {
        setMessage('Fill in the required fields: Name.');
        return;
      }
      if (corp.adminEmail && corp.adminEmail.trim() && !EMAIL_RE.test(corp.adminEmail.trim())) {
        setMessage('Enter a valid admin email address.');
        return;
      }
    }
    goToStep(Math.min(step + 1, CREATE_STEPS.length));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // In create mode, Enter and the footer button advance the current step;
    // the record is only submitted from the last step.
    if (!isEdit && step < CREATE_STEPS.length) {
      advanceStep();
      return;
    }
    if (saving) return;
    setMessage('');

    if (!isEdit) {
      const badEmail = (corp.contacts || []).some(
        (c) => c.email1 && c.email1.trim() && !EMAIL_RE.test(c.email.trim())
      );
      if (badEmail) {
        setMessage('Enter a valid email address for each contact.');
        return;
      }
    }

    setSaving(true);
    try {
      const url = isEdit && corp.id
        ? `/corps/${corp.id}`
        : '/corps';

      // Contacts are saved against the corp's own /contacts endpoints since
      // backend #148, not carried in the corp payload.
      const { contacts, ...corpPayload } = corp;
      const saved = await fetchWithCorpId(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(corpPayload)
      });

      if (saved) {
        const savedContacts = await saveContacts(
          CONTACT_OWNERS.CORP, saved.id || corp.id, contacts, loadedContactsRef.current);
        loadedContactsRef.current = savedContacts;
        initialSnapshotRef.current = JSON.stringify({ ...corp, contacts: savedContacts });
        toast.success(saved.adminAccountMessage
          ? 'Corporation saved. Admin invite sent by email.'
          : (isEdit ? 'Corporation updated' : 'Corporation created'));
        onSuccess?.();
      } else {
        setMessage('Failed to save. Please try again.');
      }
    } catch (error) {
      setMessage(error.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const tabStyle = (tab) => ({
    padding: '10px 18px',
    cursor: 'pointer',
    background: activeTab === tab ? 'var(--surface-card)' : 'transparent',
    color: activeTab === tab ? 'var(--ink)' : 'var(--muted)',
    border: 'none',
    borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
    fontFamily: 'var(--font-sans)',
    fontSize: '14px',
    fontWeight: 500,
    letterSpacing: 0,
    transition: 'background 0.16s, color 0.16s',
  });

  if (loadingRecord) {
    return <FormLoading label="Loading corporation…" />;
  }

  const contactsEditor = (
    <ContactPanel
      contacts={corp.contacts || []}
      setContacts={(updatedContacts) => setCorp(prev => ({ ...prev, contacts: updatedContacts }))}
    />
  );

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      noValidate={!isEdit}
      data-dirty={isDirty ? 'true' : 'false'}
    >
      {message && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      {isEdit ? (
        <>
          <div role="tablist" style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--hairline)', marginBottom: '24px' }}>
            <button type="button" role="tab" aria-selected={activeTab === 'info'} style={tabStyle('info')} onClick={() => setActiveTab('info')}>Info</button>
            <button type="button" role="tab" aria-selected={activeTab === 'contact'} style={tabStyle('contact')} onClick={() => setActiveTab('contact')}>
              Contacts
              {!hasContacts && <TabAttentionDot label="No contacts" />}
            </button>
          </div>

          {activeTab === 'info' && (
            <CorpInfo corp={corp} onChange={handleChange} />
          )}
          {activeTab === 'contact' && (
            <>
              <FlowNote>{CONTACTS_RULE}</FlowNote>
              {contactsEditor}
            </>
          )}

          {!hideActions && (
            <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              {onCancel && (
                <button type="button" onClick={onCancel} className="btn-secondary" disabled={saving}>Cancel</button>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <StepFlow steps={CREATE_STEPS} current={step} onStepClick={goToStep} />

          {step === 1 && (
            <CorpInfo corp={corp} onChange={handleChange} />
          )}
          {step === 2 && (
            <>
              <FlowNote>{CONTACTS_RULE}</FlowNote>
              {contactsEditor}
            </>
          )}

          {/* The stepper owns its footer in both page and drawer mode. */}
          <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
            {step === 1 && !hideActions && onCancel && (
              <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
            )}
            {step > 1 && (
              <button type="button" className="btn-secondary" onClick={() => goToStep(step - 1)} disabled={saving}>
                Back
              </button>
            )}
            <button type="submit" className="btn-primary" disabled={saving}>
              {step === 1 ? 'Next: Contacts' : (saving ? 'Saving…' : 'Create corporation')}
            </button>
          </div>
        </>
      )}
    </form>
  );
}

export default CorpForm;
